import React, { useMemo } from 'react';
import { Form, Row } from 'react-bootstrap';
import BTable from 'react-bootstrap/Table';
import { useTable, usePagination, useSortBy, useFilters, useGlobalFilter } from 'react-table';
import { faSortDown, faSortUp, faAnglesLeft, faAngleLeft, faAngleRight, faAnglesRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DefaultColumnFilter from './datatable.columnfilter';
import { matchSorter } from 'match-sorter';

function DataTable({ tableColumns, tableData, initialState }) {

    const columns = useMemo(() => tableColumns, [tableColumns]);
    const data = useMemo(() => tableData, [tableData]);

    const filterTypes = useMemo(() => ({
        // Add a new fuzzyTextFilterFn filter type.
        fuzzyText: fuzzyTextFilterFn,
        // Or, override the default text filter to use "startWith"
        text: (rows, id, filterValue) => {
            return rows.filter(row => {
                const rowValue = row.values[id]
                return rowValue !== undefined
                    ? String(rowValue)
                        .toLowerCase()
                        .startsWith(String(filterValue).toLowerCase())
                    : true
            })
        },
    }), []);

    const defaultColumn = useMemo(() => ({
        // Set default Filter UI
        Filter: DefaultColumnFilter,
    }), []);

    const {
        page,
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable(
        {
            columns,
            data,
            initialState: initialState,
            render: { type: "link" },
            defaultColumn,
            filterTypes,
            autoResetFilters: false,
            autoResetHiddenColumns: false,
            autoResetSortBy: false,
            autoResetPage: false
        },
        // When these following properties are to set true, the data needs to be memoized
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const Pagination = () => {
        return (
            <div className="d-flex justify-content-end align-items-center column-gap-3">
                <div>
                    <Form.Select
                        id="pageSize"
                        size="sm"
                        value={pageSize}
                        onChange={e => {
                            setPageSize(Number(e.target.value))
                        }}>
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </Form.Select>
                </div>
                <span>
                    Page {pageIndex + 1} of {pageOptions.length}
                </span>
                <div className="pagination">
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        <FontAwesomeIcon icon={faAnglesLeft} />
                    </button>
                    <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                    <button onClick={() => nextPage()} disabled={!canNextPage}>
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                    <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                        <FontAwesomeIcon icon={faAnglesRight} />
                    </button>
                </div>
            </div>
        )
    };

    return (
        <>
            <Row>
                <BTable responsive className="table table-light" {...getTableProps()}>
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps()}>
                                        <div  {...column.getHeaderProps(column.getSortByToggleProps())} className="disable-select" >
                                            <span className="float-end">
                                                {column.isSorted ? column.isSortedDesc ? <FontAwesomeIcon icon={faSortDown} /> : <FontAwesomeIcon icon={faSortUp} /> : <div className="invisible"><FontAwesomeIcon icon={faSortUp} /></div>}
                                            </span>
                                            {column.render('Header')}
                                        </div>
                                        <div>{column.canFilter ? column.render('Filter') : null}</div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row)
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => {
                                        return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    })}
                                </tr>
                            )
                        })}
                    </tbody>
                </BTable>
            </Row>
            <Row>
                <div className="float-end">
                    <Pagination />
                </div>
            </Row >
        </>
    );
}

function fuzzyTextFilterFn(rows, id, filterValue) {
    return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = val => !val

export default DataTable;